import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Layers, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

export function AuthPage() {
    const navigate = useNavigate();
    const { login, register } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('login');
    const [showResendOption, setShowResendOption] = useState(false);
    const [unverifiedEmail, setUnverifiedEmail] = useState('');
    const emailCheckTimeoutRef = useRef(null);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (emailCheckTimeoutRef.current) {
                clearTimeout(emailCheckTimeoutRef.current);
            }
        };
    }, []);

    // Login form
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    // Register form
    const [registerName, setRegisterName] = useState('');
    const [registerEmail, setRegisterEmail] = useState('');
    const [registerPassword, setRegisterPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    
    // Validation states
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [confirmTouched, setConfirmTouched] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const [emailExists, setEmailExists] = useState(false);
    const [checkingEmail, setCheckingEmail] = useState(false);

    const getPasswordValidation = (password) => {
        return {
            minLength: password.length >= 8,
            hasUpperCase: /[A-Z]/.test(password),
            hasLowerCase: /[a-z]/.test(password),
            hasNumber: /\d/.test(password),
        };
    };

    const passwordValidation = getPasswordValidation(registerPassword);
    const allPasswordRequirementsMet = Object.values(passwordValidation).every(v => v);
    const passwordsMatch = registerPassword === confirmPassword && confirmPassword.length > 0;
    const passwordsDontMatch = confirmPassword.length > 0 && registerPassword !== confirmPassword;

    const validatePassword = (password) => {
        if (password.length < 8) {
            return 'Password must be at least 8 characters';
        }
        if (!/[A-Z]/.test(password)) {
            return 'Password must contain at least one uppercase letter';
        }
        if (!/[a-z]/.test(password)) {
            return 'Password must contain at least one lowercase letter';
        }
        if (!/\d/.test(password)) {
            return 'Password must contain at least one number';
        }
        return null;
    };

    const checkEmailAvailability = async (email) => {
        if (!email || !email.includes('@')) {
            setEmailExists(false);
            return;
        }

        setCheckingEmail(true);
        try {
            const response = await api.request(`/auth/check-email?email=${encodeURIComponent(email)}`);
            setEmailExists(response.exists);
        } catch (err) {
            // If check fails, don't block signup
            setEmailExists(false);
        } finally {
            setCheckingEmail(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setShowResendOption(false);
        
        if (!loginEmail || !loginPassword) {
            toast.error('Please fill in all fields');
            return;
        }

        setIsLoading(true);
        try {
            await login(loginEmail, loginPassword);
            toast.success('Welcome back!');
        } catch (err) {
            const errorMessage = err.message || 'Login failed';
            
            // Check if it's an unverified email error
            if (errorMessage.toLowerCase().includes('verify') || err.status === 403) {
                setUnverifiedEmail(loginEmail);
                setShowResendOption(true);
                toast.error('Please verify your email before logging in');
            } else {
                toast.error(errorMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendVerification = async () => {
        if (!unverifiedEmail) return;
        
        setIsLoading(true);
        try {
            await api.request('/auth/resend-verification', {
                method: 'POST',
                body: JSON.stringify({ email: unverifiedEmail }),
            });
            toast.success('Verification email sent! Please check your inbox.');
            navigate('/check-email', { state: { email: unverifiedEmail } });
        } catch (err) {
            toast.error(err.message || 'Failed to resend email');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        
        // Check all required fields
        if (!registerName || !registerEmail || !registerPassword || !confirmPassword) {
            toast.error('Please fill in all fields');
            return;
        }

        // Check if email already exists
        if (emailExists) {
            toast.error('This email is already registered. Please log in instead.');
            return;
        }

        // Validate password strength
        const passwordError = validatePassword(registerPassword);
        if (passwordError) {
            toast.error(passwordError);
            return;
        }

        // Check if passwords match
        if (registerPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        setIsLoading(true);
        try {
            const response = await register(registerEmail, registerPassword, registerName);
            // Redirect to check email page
            navigate('/check-email', { state: { email: registerEmail } });
        } catch (err) {
            toast.error(err.message || 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
            {/* Logo */}
            <div className="flex items-center gap-2 mb-8">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                    <Layers className="w-5 h-5 text-primary-foreground" />
                </div>
                <h1 className="text-2xl font-bold text-foreground">Stacks</h1>
            </div>

            {/* Auth card */}
            <div className="w-full max-w-sm">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="w-full mb-6">
                        <TabsTrigger value="login" className="flex-1">Log in</TabsTrigger>
                        <TabsTrigger value="register" className="flex-1">Sign up</TabsTrigger>
                    </TabsList>

                    {/* Login Tab */}
                    <TabsContent value="login">
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <Label htmlFor="login-email" className="text-sm font-medium mb-1.5 block">
                                    Email
                                </Label>
                                <Input
                                    id="login-email"
                                    type="email"
                                    value={loginEmail}
                                    onChange={(e) => setLoginEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                />
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <Label htmlFor="login-password" className="text-sm font-medium">
                                        Password
                                    </Label>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/forgot-password')}
                                        className="text-xs text-primary hover:underline"
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                                <Input
                                    id="login-password"
                                    type="password"
                                    value={loginPassword}
                                    onChange={(e) => setLoginPassword(e.target.value)}
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                />
                            </div>

                            <Button type="submit" className="w-full h-11" disabled={isLoading}>
                                {isLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    'Log in'
                                )}
                            </Button>

                            {showResendOption && (
                                <div className="mt-3 p-3 bg-muted/50 border border-border rounded-lg">
                                    <p className="text-sm text-muted-foreground mb-2">
                                        Haven't received the verification email?
                                    </p>
                                    <Button 
                                        type="button"
                                        onClick={handleResendVerification}
                                        variant="outline"
                                        size="sm"
                                        className="w-full"
                                        disabled={isLoading}
                                    >
                                        Resend verification email
                                    </Button>
                                </div>
                            )}
                        </form>
                    </TabsContent>

                    {/* Register Tab */}
                    <TabsContent value="register">
                        <form onSubmit={handleRegister} className="space-y-4">
                            <div>
                                <Label htmlFor="register-name" className="text-sm font-medium mb-1.5 block">
                                    Name <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="register-name"
                                    type="text"
                                    value={registerName}
                                    onChange={(e) => setRegisterName(e.target.value)}
                                    placeholder="Your name"
                                    autoComplete="name"
                                    required
                                />
                            </div>

                            <div>
                                <Label htmlFor="register-email" className="text-sm font-medium mb-1.5 block">
                                    Email <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="register-email"
                                    type="email"
                                    value={registerEmail}
                                    onChange={(e) => {
                                        const email = e.target.value;
                                        setRegisterEmail(email);
                                        setEmailTouched(true);

                                        // Debounce email check using ref
                                        if (emailCheckTimeoutRef.current) {
                                            clearTimeout(emailCheckTimeoutRef.current);
                                        }
                                        emailCheckTimeoutRef.current = setTimeout(() => {
                                            checkEmailAvailability(email);
                                        }, 500);
                                    }}
                                    onBlur={() => setEmailTouched(true)}
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                    className={emailTouched && emailExists ? 'border-destructive' : ''}
                                    required
                                />
                                {emailTouched && checkingEmail && (
                                    <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        <span>Checking availability...</span>
                                    </p>
                                )}
                                {emailTouched && !checkingEmail && emailExists && (
                                    <p className="text-xs text-destructive mt-1.5 flex items-center gap-1">
                                        <span>✗</span>
                                        <span>This email is already registered</span>
                                    </p>
                                )}
                                {emailTouched && !checkingEmail && !emailExists && registerEmail.includes('@') && (
                                    <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                                        <span>✓</span>
                                        <span>Email available</span>
                                    </p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="register-password" className="text-sm font-medium mb-1.5 block">
                                    Password <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="register-password"
                                    type="password"
                                    value={registerPassword}
                                    onChange={(e) => {
                                        setRegisterPassword(e.target.value);
                                        setPasswordTouched(true);
                                    }}
                                    onBlur={() => setPasswordTouched(true)}
                                    placeholder="8+ chars, uppercase, lowercase, number"
                                    autoComplete="new-password"
                                    className={passwordTouched && !allPasswordRequirementsMet ? 'border-destructive' : ''}
                                    required
                                />
                                {passwordTouched && registerPassword.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                        <div className={`text-xs flex items-center gap-1.5 ${passwordValidation.minLength ? 'text-green-600' : 'text-destructive'}`}>
                                            <span>{passwordValidation.minLength ? '✓' : '✗'}</span>
                                            <span>At least 8 characters</span>
                                        </div>
                                        <div className={`text-xs flex items-center gap-1.5 ${passwordValidation.hasUpperCase ? 'text-green-600' : 'text-destructive'}`}>
                                            <span>{passwordValidation.hasUpperCase ? '✓' : '✗'}</span>
                                            <span>One uppercase letter</span>
                                        </div>
                                        <div className={`text-xs flex items-center gap-1.5 ${passwordValidation.hasLowerCase ? 'text-green-600' : 'text-destructive'}`}>
                                            <span>{passwordValidation.hasLowerCase ? '✓' : '✗'}</span>
                                            <span>One lowercase letter</span>
                                        </div>
                                        <div className={`text-xs flex items-center gap-1.5 ${passwordValidation.hasNumber ? 'text-green-600' : 'text-destructive'}`}>
                                            <span>{passwordValidation.hasNumber ? '✓' : '✗'}</span>
                                            <span>One number</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="confirm-password" className="text-sm font-medium mb-1.5 block">
                                    Confirm Password <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="confirm-password"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        setConfirmTouched(true);
                                    }}
                                    onBlur={() => setConfirmTouched(true)}
                                    placeholder="Re-enter your password"
                                    autoComplete="new-password"
                                    className={confirmTouched && passwordsDontMatch ? 'border-destructive' : ''}
                                    required
                                />
                                {confirmTouched && passwordsDontMatch && (
                                    <p className="text-xs text-destructive mt-1.5 flex items-center gap-1">
                                        <span>✗</span>
                                        <span>Passwords do not match</span>
                                    </p>
                                )}
                                {confirmTouched && passwordsMatch && (
                                    <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                                        <span>✓</span>
                                        <span>Passwords match</span>
                                    </p>
                                )}
                            </div>

                            <Button type="submit" className="w-full h-11" disabled={isLoading}>
                                {isLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    'Create account'
                                )}
                            </Button>
                        </form>
                    </TabsContent>
                </Tabs>

                {/* Footer */}
                <p className="text-center text-xs text-muted-foreground mt-6">
                    Build better habits, one stack at a time.
                </p>
            </div>
        </div>
    );
}
